import React, { useEffect, useState } from "react";
import {
  Box,
  VStack,
  Text,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Button,
  Flex,
  useToast,
  Skeleton,
  Stack,
  Badge,
  Input,
  InputGroup,
  InputLeftElement,
} from "@chakra-ui/react";
import { FiSearch } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const SERVER = import.meta.env.VITE_API_URL || "https://api.travelprym.in";

const LeadsList = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [vehicleSelected, setVehicleSelected] = useState(false);
  const [selectedVehicleData, setSelectedVehicleData] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [columnFilters, setColumnFilters] = useState({
    customer: "",
    mobile: "",
    from: "",
    to: "",
    pickupDate: "",
    vehicle: "",
    status: "",
  });

  useEffect(() => {
    const bookingData = localStorage.getItem("lead_booking_data");
    if (bookingData) {
      setVehicleSelected(true);
      setSelectedVehicleData(JSON.parse(bookingData));
      toast({
        title: "Vehicle Selected",
        description:
          "Select a lead below or create a new one to continue booking",
        status: "info",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [toast]);

  // Remove auto-fetch on mount - only fetch when user searches
  useEffect(() => {
    // Auto-fetch leads on mount so table is visible
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("prima_session");
      if (!token) throw new Error("Token not found");

      const res = await axios.get(`${SERVER}/management/leads`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // console.log("=== Available Leads API Response ===");
      // console.log("Full Response:", res);
      // console.log("Response Data:", res.data);
      // console.log("Leads Data:", res.data.data);
      // console.log("Total Leads Count:", res.data.data?.length || res.data?.length || 0);
      // console.log("First Lead Sample:", res.data.data?.[0] || res.data?.[0]);
      // console.log("====================================");

      const leadsData = res.data.data || res.data || [];
      setLeads(leadsData);

      // console.log("Leads set in state:", leadsData.length, "items");
    } catch (error) {
      console.error("Error fetching leads:", error);
      console.error("Error Response:", error.response);
      toast({
        title: "Error fetching leads",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLeadSelect = (lead) => {
    if (vehicleSelected) {
      // Store the selected lead ID and complete lead data
      localStorage.setItem("selectedLeadId", lead._id);
      localStorage.setItem("selectedLeadData", JSON.stringify(lead));
      // console.log("Storing lead data for booking:", lead);
      // Navigate to merged booking details page
      navigate("/bookings/lead/booking-details");
    } else {
      // No vehicle selected, just view lead details
      navigate(`/bookings/lead/edit/${lead._id}`);
    }
  };

  const handleNewLead = () => {
    if (vehicleSelected) {
      // Clear any previously selected lead
      localStorage.removeItem("selectedLeadId");
      // Go directly to merged booking details to create new customer
      navigate("/bookings/lead/booking-details");
    } else {
      // Normal flow - go to search
      navigate("/bookings/lead/search");
    }
  };

  const handleCancelSelection = () => {
    localStorage.removeItem("lead_booking_data");
    localStorage.removeItem("selectedVehicleId");
    localStorage.removeItem("lead_passengers");
    setVehicleSelected(false);
    setSelectedVehicleData(null);
  };

  // Filter leads based on search query and column filters
  const visibleLeads = leads.filter(
    (lead) => lead.status === undefined || lead.status === null,
  );

  const filteredLeads = visibleLeads.filter((lead) => {
    const query = searchQuery.toLowerCase();
    const customerName =
      lead.customerID?.name?.toLowerCase() ||
      lead.customer?.name?.toLowerCase() ||
      lead.name?.toLowerCase() ||
      "";
    const customerMobile =
      lead.customerID?.mobile?.toLowerCase() ||
      lead.customer?.mobile?.toLowerCase() ||
      lead.mobile?.toLowerCase() ||
      "";

    // Search query filter
    const matchesSearch =
      customerName.includes(query) || customerMobile.includes(query);

    // Column filters
    const customerFilter = customerName.includes(
      columnFilters.customer.toLowerCase(),
    );
    const mobileFilter = customerMobile.includes(
      columnFilters.mobile.toLowerCase(),
    );
    const fromLocation =
      (typeof lead.fromLocation === "object"
        ? lead.fromLocation?.address
        : lead.fromLocation) || "";
    const fromFilter = fromLocation
      .toLowerCase()
      .includes(columnFilters.from.toLowerCase());
    const toLocation =
      (typeof lead.toLocation === "object"
        ? lead.toLocation?.address
        : lead.toLocation) || "";
    const toFilter = toLocation
      .toLowerCase()
      .includes(columnFilters.to.toLowerCase());

    // Format pickup date for filtering (same as display format)
    let pickupDate = "";
    if (lead.pickupDateTime) {
      try {
        pickupDate = new Date(lead.pickupDateTime)
          .toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          })
          .toLowerCase();
      } catch (e) {
        pickupDate = "";
      }
    }
    const pickupDateFilter = pickupDate.includes(
      columnFilters.pickupDate.toLowerCase(),
    );

    const vehicle = lead.vehicleSegment?.segmentName || "";
    const vehicleFilter = vehicle
      .toLowerCase()
      .includes(columnFilters.vehicle.toLowerCase());
    const status = lead.status === "Converted" ? "Converted" : "Active";

    const statusFilter = status
      .toLowerCase()
      .includes(columnFilters.status.toLowerCase());

    return (
      matchesSearch &&
      customerFilter &&
      mobileFilter &&
      fromFilter &&
      toFilter &&
      pickupDateFilter &&
      vehicleFilter &&
      statusFilter
    );
  });

  // Log first lead structure for debugging
  useEffect(() => {
    if (leads.length > 0) {
      // console.log("=== First Lead Structure ===");
      // console.log("Full Lead Object:", leads[0]);
      // console.log("Customer Data:", leads[0].customerID || leads[0].customer || "No customer object found");
      // console.log("Customer Name:", leads[0].customerID?.name || leads[0].customer?.name || leads[0].name || "No name");
      // console.log("Customer Mobile:", leads[0].customerID?.mobile || leads[0].customer?.mobile || leads[0].mobile || "No mobile");
      // console.log("All Keys in Lead:", Object.keys(leads[0]));
      // console.log("===========================");
    }
  }, [leads]);

  return (
    <VStack
      w="100%"
      align="stretch"
      p={6}
      bg="#eef3fb"
      spacing={6}
      minH="100vh"
    >
      <Flex justify="space-between" align="center">
        <Text fontSize="2xl" fontWeight="bold">
          Select Lead
        </Text>
        <Flex gap={3}>
          {vehicleSelected && (
            <Button
              onClick={() => {
                handleCancelSelection();
                navigate("/bookings/lead/results");
              }}
              size="sm"
              variant="outline"
              colorScheme="blue"
            >
              Change Vehicle
            </Button>
          )}
          <Button onClick={handleNewLead} size="sm" colorScheme="blue">
            {vehicleSelected ? "New Lead" : "Create Lead"}
          </Button>
        </Flex>
      </Flex>

      {/* Vehicle Selection Banner */}
      {vehicleSelected && selectedVehicleData && (
        <Box
          bg="blue.50"
          p={4}
          borderRadius="lg"
          border="1px solid"
          borderColor="blue.200"
        >
          <Flex justify="space-between" align="center">
            <VStack align="start" spacing={1}>
              <Text fontWeight="bold" color="blue.700">
                🚗 {selectedVehicleData?.carDetails?.name} Selected
              </Text>
              <Text fontSize="sm" color="blue.600">
                Estimated Price: ₹{selectedVehicleData?.estimatedPrice || 0} •
                Distance: {selectedVehicleData?.distance || 0} km
              </Text>
              <Text fontSize="sm" color="blue.600">
                Click on a lead below to continue with that customer, or create
                a new lead
              </Text>
            </VStack>
            <Button
              size="sm"
              colorScheme="red"
              variant="ghost"
              onClick={handleCancelSelection}
            >
              Cancel
            </Button>
          </Flex>
        </Box>
      )}

      {/* Leads Table */}
      <Box bg="white" p={6} borderRadius="lg" shadow="md">
        <Flex justify="space-between" align="center" mb={4}>
          <Text fontSize="lg" fontWeight="bold">
            Search Leads
          </Text>
          <InputGroup maxW="300px">
            <InputLeftElement pointerEvents="none">
              <FiSearch color="gray.400" />
            </InputLeftElement>
            <Input
              placeholder="Search by Name or Mobile"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                // Fetch leads when user starts searching
                if (e.target.value.length > 0 && leads.length === 0) {
                  fetchLeads();
                }
              }}
              size="md"
            />
          </InputGroup>
        </Flex>

        {loading ? (
          <Stack spacing={4} p={10}>
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} height="80px" borderRadius="md" />
            ))}
          </Stack>
        ) : leads.length === 0 ? (
          <Flex justify="center" p={10}>
            <VStack>
              <Text color="gray.500">No leads found</Text>
              <Button
                onClick={handleNewLead}
                size="sm"
                colorScheme="blue"
                mt={3}
              >
                Create New Lead
              </Button>
            </VStack>
          </Flex>
        ) : filteredLeads.length === 0 && searchQuery ? (
          <Flex justify="center" p={10}>
            <VStack>
              <Text color="gray.500">
                No leads match your search or filters
              </Text>
            </VStack>
          </Flex>
        ) : (
          <Table variant="simple" size="sm">
            <Thead>
              <Tr>
                <Th>Customer</Th>
                <Th>Mobile</Th>
                <Th>From</Th>
                <Th>To</Th>
                <Th>Pickup Date</Th>
                <Th>Vehicle</Th>
                <Th>Status</Th>
                <Th>Action</Th>
              </Tr>
              {/* Filter Row */}
              <Tr bg="gray.50">
                <Th p={2}>
                  <Input
                    size="xs"
                    placeholder="Filter..."
                    value={columnFilters.customer}
                    onChange={(e) =>
                      setColumnFilters({
                        ...columnFilters,
                        customer: e.target.value,
                      })
                    }
                    onClick={(e) => e.stopPropagation()}
                    fontSize="xs"
                  />
                </Th>
                <Th p={2}>
                  <Input
                    size="xs"
                    placeholder="Filter..."
                    value={columnFilters.mobile}
                    onChange={(e) =>
                      setColumnFilters({
                        ...columnFilters,
                        mobile: e.target.value,
                      })
                    }
                    onClick={(e) => e.stopPropagation()}
                    fontSize="xs"
                  />
                </Th>
                <Th p={2}>
                  <Input
                    size="xs"
                    placeholder="Filter..."
                    value={columnFilters.from}
                    onChange={(e) =>
                      setColumnFilters({
                        ...columnFilters,
                        from: e.target.value,
                      })
                    }
                    onClick={(e) => e.stopPropagation()}
                    fontSize="xs"
                  />
                </Th>
                <Th p={2}>
                  <Input
                    size="xs"
                    placeholder="Filter..."
                    value={columnFilters.to}
                    onChange={(e) =>
                      setColumnFilters({ ...columnFilters, to: e.target.value })
                    }
                    onClick={(e) => e.stopPropagation()}
                    fontSize="xs"
                  />
                </Th>
                <Th p={2}>
                  <Input
                    size="xs"
                    placeholder="Filter..."
                    value={columnFilters.pickupDate}
                    onChange={(e) =>
                      setColumnFilters({
                        ...columnFilters,
                        pickupDate: e.target.value,
                      })
                    }
                    onClick={(e) => e.stopPropagation()}
                    fontSize="xs"
                  />
                </Th>
                <Th p={2}>
                  <Input
                    size="xs"
                    placeholder="Filter..."
                    value={columnFilters.vehicle}
                    onChange={(e) =>
                      setColumnFilters({
                        ...columnFilters,
                        vehicle: e.target.value,
                      })
                    }
                    onClick={(e) => e.stopPropagation()}
                    fontSize="xs"
                  />
                </Th>
                <Th p={2}>
                  <Input
                    size="xs"
                    placeholder="Filter..."
                    value={columnFilters.status}
                    onChange={(e) =>
                      setColumnFilters({
                        ...columnFilters,
                        status: e.target.value,
                      })
                    }
                    onClick={(e) => e.stopPropagation()}
                    fontSize="xs"
                  />
                </Th>
                <Th></Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredLeads.map((lead) => (
                <Tr
                  key={lead._id}
                  _hover={{ bg: vehicleSelected ? "blue.50" : "gray.50" }}
                  cursor="pointer"
                  onClick={() => handleLeadSelect(lead)}
                >
                  <Td>
                    {lead.customerID?.name ||
                      lead.customer?.name ||
                      lead.name ||
                      "-"}
                  </Td>
                  <Td>
                    {lead.customerID?.mobile ||
                      lead.customer?.mobile ||
                      lead.mobile ||
                      "-"}
                  </Td>
                  <Td>
                    {typeof lead.fromLocation === "object"
                      ? lead.fromLocation?.address || "-"
                      : lead.fromLocation || "-"}
                  </Td>
                  <Td>
                    {typeof lead.toLocation === "object"
                      ? lead.toLocation?.address || "-"
                      : lead.toLocation || "-"}
                  </Td>
                  <Td>
                    {lead.pickupDateTime
                      ? new Date(lead.pickupDateTime).toLocaleDateString()
                      : "-"}
                  </Td>
                  <Td>{lead.vehicleSegment?.segmentName || "-"}</Td>
                  <Td>
                    <Badge
                      colorScheme={
                        lead.status === "Converted" ? "green" : "yellow"
                      }
                    >
                      {lead.status === "Converted" ? "Converted" : "Active"}
                    </Badge>
                  </Td>
                  <Td>
                    <Button
                      size="sm"
                      colorScheme="blue"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleLeadSelect(lead);
                      }}
                    >
                      {vehicleSelected ? "Select" : "View"}
                    </Button>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Box>
    </VStack>
  );
};

export default LeadsList;
